/**
 * Sky decor seeds and mockup sun-arc geometry shared by the hour clock.
 * Color phase and orbit progress live in `core/sky-clock.js`.
 */

import { resolveSkyAnchorColumns } from "../layout/responsive-board.js";

export function clamp01(v) {
  if (v <= 0) return 0;
  if (v >= 1) return 1;
  return v;
}

/** Sea horizon as fraction of grid / play height from top (day.md: 95% = 5% from bottom). */
export const MOCKUP_SEA_LINE_RATIO = 0.95;

/**
 * Default sun disc radius when layout scale is unknown (mockup 120px diameter @ scale 1).
 */
export const CELESTIAL_DEFAULT_BODY_RADIUS_PX = 60;

/**
 * Summit depth below the sea line: at least one full disc radius so p=0/1 start hidden.
 * @param {{ width: number, height: number }} core
 * @param {number} [bodyRadius]
 */
export function computeSummitBelowOffset(core, bodyRadius) {
  var r = Math.max(0, Number(bodyRadius) || CELESTIAL_DEFAULT_BODY_RADIUS_PX);
  var layoutMargin = Math.max(core.height * 0.06, core.width * 0.11);
  // Sit the summit a comfortable buffer below the sea line (a full disc plus
  // half again) so the body is for sure fully hidden at p=0/1, not just grazing
  // the horizon.
  return Math.max(layoutMargin, r + 4) + r * 0.5;
}

/**
 * Whether the celestial disc (and sea glare) should render.
 * Symmetric on both halves: the disc is drawn whenever any part of it is above
 * the sea line, so it rises up out of the water and sinks back behind it instead
 * of popping in/out fully formed. The disc gets clipped at the sea line by the
 * caller, so drawing it while still partly (or fully) submerged is harmless.
 * @param {number} y disc centre Y
 * @param {number} seaLineY
 * @param {number} bodyRadius
 * @param {number} [progress] arc progress 0..1 — accepted for back-compat, unused
 */
export function isCelestialDiscVisible(y, seaLineY, bodyRadius, progress) {
  var r = Math.max(0, Number(bodyRadius) || 0);
  if (r <= 0) return y < seaLineY;
  return (y - r) < seaLineY;
}

/**
 * Per-run seed combined with cycle index → deterministic decor offsets.
 * @param {number} runSeed
 * @param {number} cycleIndex
 */
export function combineSkyDecorSeed(runSeed, cycleIndex) {
  var s = Math.floor(Number(runSeed) || 0);
  var c = Math.max(0, Math.floor(Number(cycleIndex) || 0));
  return ((s * 1664525 + c * 1013904223) >>> 0);
}

/**
 * Deterministic 0..1 hash from seed + index.
 * @param {number} seed
 * @param {number} n
 */
export function seededHash01(seed, n) {
  var s = Math.sin((seed + n * 12.9898) * 0.0001 + n * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Responsive sun-arc anchors from layout strips (mockup: rise L of evo ring, set R of stash).
 * @param {{
 *   coreRect?: { x: number, y: number, width: number, height: number },
 *   playArea?: { x: number, y: number, width: number, height: number },
 *   leftStrip?: { x: number, width: number },
 *   rightStrip?: { x: number, width: number },
 *   gridUnitPx?: number,
 *   gridWidthPx?: number,
 * }} layout
 */
export function computeSunArcAnchors(layout, opts) {
  layout = layout || {};
  opts = opts || {};
  var sky = resolveSkyAnchorColumns(layout);
  var core = sky.coreRect;
  var play = sky.playArea;
  var left = sky.leftStrip;
  var right = sky.rightStrip;

  var seaLineY;
  if (layout.layoutMode === "vertical" && play && isFinite(play.y) && isFinite(play.height)) {
    seaLineY = play.y + play.height * MOCKUP_SEA_LINE_RATIO;
  } else {
    seaLineY = core.y + core.height * MOCKUP_SEA_LINE_RATIO;
  }
  var belowOffset = computeSummitBelowOffset(core, opts.bodyRadius);

  return {
    rise: {
      x: left.x + left.width * 0.55,
      y: seaLineY + belowOffset,
    },
    apex: {
      x: play.x + play.width * 0.5,
      y: play.y - Math.max(8, play.height * 0.02),
    },
    set: {
      x: right.x + right.width * 0.45,
      y: seaLineY + belowOffset,
    },
    seaLineY: seaLineY,
  };
}

/**
 * Elliptical sun position for Fall arc progress.
 * θ = π·(1−p): rise at p=0, apex at p≈0.5, set at p=1.
 * @param {ReturnType<typeof computeSunArcAnchors>} anchors
 * @param {number} fallProgress 0..1
 * @param {{ bodyRadius?: number }} [opts]
 */
export function computeSunPosition(anchors, fallProgress, opts) {
  opts = opts || {};
  var p = clamp01(fallProgress);
  var rise = anchors.rise;
  var set = anchors.set;
  var apex = anchors.apex;

  var cx = (rise.x + set.x) * 0.5;
  var a = (set.x - rise.x) * 0.5;
  var cy = rise.y;
  var b = Math.max(1, cy - apex.y);

  var theta = Math.PI * (1 - p);
  var x = cx + a * Math.cos(theta);
  var y = cy - b * Math.sin(theta);

  var bodyRadius = Math.max(0, Number(opts.bodyRadius) || 0);
  var visible = isCelestialDiscVisible(y, anchors.seaLineY, bodyRadius, p);

  return { x: x, y: y, visible: visible, progress: p, theta: theta };
}
